import React from 'react';

import { IMilestoneFields } from '../../../types/contentful';
import { Block, BLOCKS, Inline, INLINES } from '@contentful/rich-text-types';

import RichText from '../RichText';
import cx from 'classnames';

import ImageLink from '../ImageLink';
import { normalizeHexCode } from '~/styles/utils';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import { Asset } from '../AtomicComponents/Asset';
import { Eyebrow } from '../Eyebrow';
import { themeColors } from './theme';
import { ArrowIcon } from '../AtomicComponents/TextLink';

interface Props extends IMilestoneFields {
  sysId?: string;
}

export function Milestone({
  headingText,
  eyebrow,
  image,
  imageLink,
  bodyCopy,
  disclaimer,
  backgroundColor,
  textColor,
  sysId,
  imageFirst,
  anchorLink,
  imageCornerRounding,
  imageDesktopFlushToEdges,
  imageMobileFlushToEdges,
  colorTheme,
  bodyLink
}: Props) {
  const hasTheme = Boolean(colorTheme);
  const themeKey = colorTheme as keyof typeof themeColors;
  const themeColor = hasTheme ? themeColors[themeKey] : undefined;
  const RICH_TEXT_OVERRIDE: RenderNode = {
    [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => {
      return (
        <p
          className="typography-body-1 font-light"
          style={{
            color: hasTheme ? themeColor?.bodyText : undefined
          }}
        >
          {children}
        </p>
      );
    }
  };

  const BODY_LINK_OVERRIDE: RenderNode = {
    [INLINES.HYPERLINK]: (node: Block | Inline, children) => {
      return (
        <a
          href={node.data.uri}
          className="group inline-flex items-center font-button-md_web-desktop-max text-button-md_web-desktop-min underline decoration-[0.08em] underline-offset-[0.125em] transition-all duration-200"
          style={{
            color: hasTheme ? themeColor?.bodyText : undefined
          }}
        >
          {children}
          <ArrowIcon
            className={cx(
              'ml-1 inline-block -translate-x-1 opacity-0 transition-all duration-200 group-hover:translate-x-0.5 group-hover:opacity-100',
              hasTheme && themeColor?.bodyText === '#FFFFFF' && 'invert'
            )}
            containerClassName="inline-flex h-4 w-4 items-center justify-center align-middle"
          />
        </a>
      );
    },
    [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => {
      return (
        <p
          className="font-button-md_web-desktop-max text-button-md_web-desktop-min"
          style={{
            color: hasTheme ? themeColor?.bodyText : undefined
          }}
        >
          {children}
        </p>
      );
    }
  };

  return (
    <div
      className="block-container"
      id={anchorLink ?? `${sysId}`}
      data-blockid={`milestone-${sysId}`}
      data-testid="milestone-block"
    >
      <div className="layout layout-padding flex w-full !max-w-none items-center justify-center">
        <div
          className={cx(
            'flex w-full items-center justify-center rounded-2xl px-4 tablet:px-6',
            hasTheme && themeColor?.background
          )}
          style={{
            backgroundColor: hasTheme
              ? undefined
              : normalizeHexCode(backgroundColor) ?? 'transparent'
          }}
        >
          <div
            className={cx(
              'flex w-full flex-1 flex-col flex-wrap items-center justify-center self-stretch overflow-hidden rounded-card-shape desktop:!flex-nowrap',
              {
                'desktop:flex-row-reverse': imageFirst,
                'desktop:flex-row': !imageFirst
              }
            )}
          >
            <div
              className={cx(
                'column w-full flex-col items-center pb-4 tablet:py-4 desktop:w-1/2 desktop:py-8',
                {
                  'desktop:pl-10 desktop:pr-[72px]': imageFirst,
                  'desktop:pl-[72px] desktop:pr-10': !imageFirst
                }
              )}
            >
              <div className="flex w-full flex-col items-start px-6 pt-10 text-start desktop:pt-0">
                {eyebrow && (
                  <Eyebrow
                    eyebrowFields={eyebrow?.fields}
                    className="typography-body-2 font-[350]"
                    style={{
                      color: hasTheme ? themeColor?.bodyText : undefined
                    }}
                  />
                )}
                <div className="mb-4 desktop:max-w-[480px]">
                  <h2
                    className={cx(
                      'mobile:typography-display-2 tablet:text-[32px]',
                      hasTheme
                        ? themeColor?.headerText
                        : `text-${normalizeHexCode(textColor, true)}`
                    )}
                  >
                    {headingText}
                  </h2>
                </div>
                {bodyCopy && (
                  <div
                    className={`w-full tablet:max-w-[750px] [&>a]:mt-6 [&>a]:w-full [&>a]:max-w-[258px] ${
                      disclaimer ? 'border-box mb-6' : 'border-box'
                    }`}
                  >
                    <RichText
                      bulletColor={textColor}
                      data={bodyCopy}
                      overrideOptions={RICH_TEXT_OVERRIDE}
                    />
                  </div>
                )}
                {bodyLink && (
                  <div className="mt-6 w-full font-button-md_web-desktop-max text-button-md_web-desktop-min">
                    <RichText
                      bulletColor={textColor}
                      data={bodyLink}
                      overrideOptions={BODY_LINK_OVERRIDE}
                    />
                  </div>
                )}
                {disclaimer && (
                  <p
                    className="body-extra-small body-small max-w-[480px] whitespace-pre-wrap"
                    style={{
                      color: hasTheme ? themeColor?.bodyText : undefined
                    }}
                  >
                    {disclaimer}
                  </p>
                )}
              </div>
            </div>
            <div
              className={cx(
                'column w-full flex-col justify-center self-stretch px-4 py-4 tablet:py-4 desktop:w-1/2 desktop:py-8',
                imageMobileFlushToEdges
                  ? '!p-0 tablet:px-4 tablet:pt-4'
                  : 'px-4 pt-4',
                {
                  'tablet:pl-4 desktop:pl-8': imageFirst,
                  'tablet:pr-4 desktop:pr-8': !imageFirst,
                  'desktop:!p-0': imageDesktopFlushToEdges,
                  '[&_img]:overflow-hidden [&_img]:rounded-3xl [&_img]:desktop:rounded-4xl':
                    imageCornerRounding
                }
              )}
            >
              {imageLink ? (
                <ImageLink
                  {...{
                    ...imageLink.fields
                  }}
                >
                  {(imageAsset) => (
                    <Asset
                      asset={imageAsset}
                      className="block w-full max-w-full shrink-0"
                    />
                  )}
                </ImageLink>
              ) : (
                <Asset
                  asset={image}
                  className="block w-full max-w-full shrink-0"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
